import {
  Button,
  Divider,
  EButtonColorVariant,
  EHeadingStyleVariant,
  EToastVariant,
  Grid,
  GridItem,
  Heading,
  LinkButton,
  useIsWindowWidthGreaterThan,
} from '@outdoorsyco/bonfire';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { StickySection } from '@/components/common/StickySection/StickySection';
import { useRouter } from '@/hooks/useRouter';
import { useToast } from '@/hooks/useToast';
import { postTextVerification, updateUser } from '@/redux/modules/auth';
import { TAppDispatch } from '@/redux/rootReducer';
import { IFullUserInfo } from '@/services/types/user/user';

import { CoverPhoto } from '../Profile/CoverPhoto/CoverPhoto';
import { ProfilePhoto } from '../Profile/ProfilePhoto/ProfilePhoto';
import { About } from './About/About';
import { Address } from './Address/Address';
import { EmailAddress } from './EmailAddress/EmailAddress';
import { LegalName } from './LegalName/LegalName';
import { PasswordChange } from './PasswordChange/PasswordChange';
import { PhoneNumber } from './PhoneNumber/PhoneNumber';
import { Settings } from './Settings/Settings';

export type TPersonalInfoValues = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  description: string;
  verifyPhoneNumber1: string;
  verifyPhoneNumber2: string;
  verifyPhoneNumber3: string;
  verifyPhoneNumber4: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  localeTimezone: string;
  localeBaseCurrency: string;
};

export enum EPersonalInfoFields {
  FirstName = 'firstName',
  LastName = 'lastName',
  Email = 'email',
  PhoneNumber = 'phoneNumber',
  Description = 'description',
  VerifyPhoneNumber1 = 'verifyPhoneNumber1',
  VerifyPhoneNumber2 = 'verifyPhoneNumber2',
  VerifyPhoneNumber3 = 'verifyPhoneNumber3',
  VerifyPhoneNumber4 = 'verifyPhoneNumber4',
  address1 = 'address1',
  address2 = 'address2',
  city = 'city',
  state = 'state',
  zip = 'zip',
  country = 'country',
  LocaleTimezone = 'localeTimezone',
  LocaleBaseCurrency = 'localeBaseCurrency',
}

interface IEditProfileProps {
  userData: IFullUserInfo;
  refetchUser: () => void;
}

export const ProfileEdit = ({ userData, refetchUser }: IEditProfileProps) => {
  const intl = useIntl();
  const router = useRouter();
  const dispatch: TAppDispatch = useDispatch();
  const { showToast } = useToast();
  const desktopView = useIsWindowWidthGreaterThan('sm', true);

  const [phoneCodeError, setPhoneCodeError] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    const query = router.query && router.query['zone'];
    if (query) {
      if (query === 'Password') {
        const zone = document.getElementById('passwordChangeZone');
        zone?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      if (query === 'AboutMe') {
        const zone = document.getElementById('aboutMeZone');
        setTimeout(() => {
          // The zone is found but the scrollIntoView is not working unless it's wrapped in a setTimeout
          zone?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    control,
    setError,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, dirtyFields },
  } = useForm<TPersonalInfoValues>({
    defaultValues: {
      firstName: userData.profile.first_name,
      lastName: userData.profile.last_name,
      phoneNumber: userData.profile.phone,
      email: userData.profile.email,
      description: userData.profile.business.description,
      verifyPhoneNumber1: '',
      verifyPhoneNumber2: '',
      verifyPhoneNumber3: '',
      verifyPhoneNumber4: '',
      address1: userData.profile.address.street || '',
      address2: userData.profile.address.street_etc || '',
      city: userData.profile.address.city || '',
      state: userData.profile.address.state || '',
      zip: userData.profile.address.zip || '',
      country: userData.profile.address.country || 'US',
      localeTimezone: userData.profile.locale.timezone || '',
      localeBaseCurrency: userData.profile.locale.base_currency || 'USD',
    },
  });

  const hasErrors = Object.keys(errors).length > 0;

  const onSubmit = async (data: TPersonalInfoValues) => {
    setIsSaving(true);
    const newUser = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      phone: data.phoneNumber,
      business: { description: data.description },
      address: {
        city: data.city,
        country: data.country,
        state: data.state,
        street: data.address1,
        street_etc: data.address2,
        zip: data.zip,
      },
      locale: {
        timezone: data.localeTimezone,
        base_currency: data.localeBaseCurrency,
      },
    };

    try {
      const isSuccess = await dispatch(updateUser(newUser));

      if (!isSuccess) {
        showToast(
          EToastVariant.Error,
          intl.formatMessage({ defaultMessage: 'Error', id: 'KN7zKn' }),
          intl.formatMessage({
            defaultMessage: 'Opps, there was a error. Please try again.',
            id: 'NDvJu/',
          }),
        );

        if (dirtyFields.phoneNumber) {
          setError('phoneNumber', {
            type: 'validate',
            message: intl.formatMessage({
              defaultMessage: 'This must be a valid phone number',
              id: '4wy/pb',
            }),
          });
        }

        return;
      }

      await refetchUser();
      if (dirtyFields.phoneNumber) {
        await dispatch(postTextVerification(data.phoneNumber));
        setPhoneCodeError(false);
        // Scroll to phone number input area
        const zone = document.getElementById('phoneNumberZone');
        zone?.scrollIntoView({ behavior: 'smooth' });
      }

      if (!dirtyFields.phoneNumber) {
        router.push({ pathname: '/account' }, undefined, { shallow: true });
      }

      showToast(
        EToastVariant.Success,
        intl.formatMessage({
          defaultMessage: 'Success',
          id: 'xrKHS6',
        }),
        intl.formatMessage({
          defaultMessage: 'Your profile has been updated.',
          id: 'U+X9Ry',
        }),
      );
    } catch (error) {
      const errorResponse = error?.response?.error;
      const originalError = error?.response?.original_error;
      const errorMessage = originalError
        ? originalError
        : errorResponse
          ? errorResponse
          : intl.formatMessage({
              defaultMessage: 'Oops, something went wrong. Try again later.',
              id: '/m4g4K',
            });
      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        errorMessage,
      );

      if (dirtyFields.phoneNumber && errorMessage) {
        setError('phoneNumber', {
          type: 'validate',
          message: errorMessage,
        });
        setPhoneCodeError(true);
      } else if (
        dirtyFields.phoneNumber &&
        originalError &&
        originalError.split(':')[0] === 'PhoneAndCountry'
      ) {
        setError('phoneNumber', {
          type: 'validate',
          message: originalError
            ? originalError.split(':')[1]
            : intl.formatMessage({
                defaultMessage: 'This must be a valid phone number',
                id: '4wy/pb',
              }),
        });
        setPhoneCodeError(true);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Grid columnSpacing={2} className="mb-4">
      <GridItem sm={6} xs={12}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex items-center justify-between mb-10">
            <Heading variant={EHeadingStyleVariant.H5}>
              <FormattedMessage defaultMessage="Personal info" id="TxlewI" />
            </Heading>
          </div>
          {!desktopView && (
            <ProfilePhoto userData={userData} refetchUser={refetchUser} isLoading={isSaving} />
          )}
          <div>
            <LegalName control={control} errors={errors} />
            <Divider className="my-10" />
            <EmailAddress userData={userData} control={control} errors={errors} />
            <Divider className="my-10" />
            <PhoneNumber
              userData={userData}
              control={control}
              errors={errors}
              getValues={getValues}
              setValue={setValue}
              refreshPageUser={refetchUser}
              setPhoneCodeError={setPhoneCodeError}
              phoneCodeError={phoneCodeError}
            />
            <Divider className="my-10" />
            <About control={control} errors={errors} />
            <Divider className="my-10" />
            <Address userData={userData} control={control} errors={errors} setValue={setValue} />
            <Divider className="my-10" />
            <CoverPhoto userData={userData} refetchUser={refetchUser} isLoading={isSaving} />
            <Divider className="my-10" />
            <Settings control={control} errors={errors} />
            <Divider className="my-10" />
            <PasswordChange refreshUser={refetchUser} />
          </div>

          <StickySection>
            <Link href="/account" passHref legacyBehavior>
              <LinkButton
                label={intl.formatMessage({
                  defaultMessage: 'Cancel',
                  id: '47FYwb',
                })}
                disabled={isSaving}
              />
            </Link>
            <Button
              aria-label={intl.formatMessage({
                defaultMessage: 'Save edit button',
                id: 'uIh2fE',
              })}
              label={intl.formatMessage({
                defaultMessage: 'Save',
                id: 'jvo0vs',
              })}
              type="submit"
              variant={EButtonColorVariant.Primary}
              disabled={isSaving || hasErrors}
              loading={isSaving}
            />
          </StickySection>
        </form>
      </GridItem>
      <GridItem sm={1} xs={1} />
      {desktopView && (
        <GridItem sm={5} xs={12}>
          <ProfilePhoto userData={userData} refetchUser={refetchUser} isLoading={isSaving} />
        </GridItem>
      )}
    </Grid>
  );
};
